import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import convertToK from '../../utils/convertToK';
import formattedMoney from '../../utils/formatMoney';
import Modal from '../Modal';
import RedirectPopup from './RedirectPopup';
import lazadaApi from '../../api/lazadaApi';
import tiktokApi from '../../api/tiktokApi';
import JoinGroup from '../Modal/popup/JoinGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins, faWarning, faChevronDown, faBell } from '@fortawesome/free-solid-svg-icons';
import shopeApi from '../../api/shopeeApi';
import riohubAffiliateApi from '../../api/riohubAffiliateApi';
import { toast } from 'react-toastify';
import EarnCommission from '../../pages/Merchant/Shopee/EarnCommission';
import priceWatchApi from '../../api/priceWatchApi';
import PriceWatchAlertModal from '../../pages/PriceWatch/price-watch-alert-modal';
import AppX2CashbackNote from './AppX2CashbackNote';
import { getProductNote } from '../../api/productNotesApi';

// Điều hướng thẳng trong tab hiện tại. window.open() sau await mất user-gesture nên
// Safari/Chrome mobile chặn popup im lặng → user bấm MUA NGAY mà không thấy gì xảy ra.
const clickAffLink = (url) => {
    window.location.href = url;
};

function ProductCardMain({
    productInfo,
    isLoading,
    platform,
    onlyInfo,
    buyBtnTextColor,
    buyBtnBgColor,
    cashBackTextColor,
    source = 'pd',
    tiktokLinkMode = false,
    rewardNote,
}) {
    const { isLogined } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const [cachedAffLink, setCachedAffLink] = useState("");
    // Dòng nhắc do admin cấu hình cho sàn này (file tĩnh /img/product-notes.json).
    // Rỗng = không hiện gì. Lỗi tải cũng chỉ là không hiện, không ảnh hưởng thẻ.
    const [productNote, setProductNote] = useState("");
    const {
        productName,
        shopName,
        price,
        commission,
        imageUrl,
        sales,
        productLink,
        shopeeComFinal = 0,
        sellerComFinal = 0,
        isXtra = false,
        platformBonusNote = "", // chữ nhắc thưởng thêm do BE đẩy (rỗng = không hiện)
    } = productInfo || {};

    useEffect(() => {
        let mounted = true;
        getProductNote(platform || 'shopee').then((note) => {
            if (mounted) setProductNote(note);
        });
        return () => { mounted = false; };
    }, [platform]);
    const [onGetLink, setOnGetLink] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [openModalEarn, setOpenModalEarn] = useState(false);
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [showWatchModal, setShowWatchModal] = useState(false);
    const [isWatching, setIsWatching] = useState(false);

    const handleAddWatch = async (config) => {
        if (!isLogined) { navigate('/login'); return; }
        try {
            await priceWatchApi.addWatch({
                itemId: productInfo?.itemId,
                platform: platform || 'shopee',
                productName,
                imageUrl,
                productLink,
                currentPrice: price,
                ...config,
            });
            setIsWatching(true);
        } catch (err) {
            console.warn('[ProductCardMain] addWatch error:', err);
        } finally {
            setShowWatchModal(false);
        }
    };

    const handleSubmit = async () => {
        if (!isLogined) return navigate(`/login?redirect=${platform}`);
        setOnGetLink(true);
        if (platform === 'shopee') {
            try {
                if (!productLink) throw new Error('Thiếu link sản phẩm');
                const { data } = await shopeApi.getLinkAff(productLink, source);
                const linkAff = data?.data?.batchCustomLink?.[0]?.shortLink;
                setOnGetLink(false);
                if (linkAff) return clickAffLink(linkAff);
                throw new Error('Sàn không trả về link');
            } catch (error) {
                console.log(error);
                setOnGetLink(false);
                // Trước đây lỗi bị nuốt im lặng → nút MUA NGAY như bị "chết".
                // TUYỆT ĐỐI không mở link gốc thay thế: link chưa qua chuyển đổi là
                // link trần (không có subId/affId) → user mua xong mất hoàn tiền mà
                // không hề biết. Thà báo lỗi để họ bấm lại.
                toast.error(
                    error?.response?.data?.message ||
                        'Chưa tạo được link hoàn tiền (sàn đang quá tải). Vui lòng bấm lại sau ít giây.'
                );
                return;
            }
        }

        try {
            setOnGetLink(true);
            if (platform === 'lazada') {
                const { itemId, skuId, offerId } = productInfo;
                try {
                    const { data } = await lazadaApi.getLinkAff(itemId, skuId, offerId, source);
                    setOnGetLink(false);
                    const linkAff = data?.linkAff?.shortLink;
                    if (!linkAff) throw new Error('Sàn không trả về link');
                    clickAffLink(linkAff);
                } catch (error) {
                    console.log(error);
                    setOnGetLink(false);
                    toast.error(
                        error?.response?.data?.message ||
                            'Chưa tạo được link hoàn tiền (sàn đang quá tải). Vui lòng bấm lại sau ít giây.'
                    );
                }
            }

            if (platform === 'tiktok') {
                const { itemId } = productInfo;
                try {
                    if (tiktokLinkMode) {
                        // Link mode (RioHub): tạo affiliate link (sub_id chứa userId) → mở.
                        const link = await ensureAffiliateLink();
                        setOnGetLink(false);
                        if (link) clickAffLink(link);
                        else if (productLink) clickAffLink(productLink);
                    } else {
                        const { data } = await tiktokApi.addToShowCase(itemId);
                        setOnGetLink(false);
                        const linkAff = `https://tiktok.com/${data.showCase[0].tiktokshopId}`;
                        window.location.href = linkAff;
                    }
                } catch (error) {
                    console.log(error);
                    setOnGetLink(false);
                    // Thông báo chi tiết từ backend (vd RioHub: sản phẩm chưa có hoa hồng/chưa duyệt).
                    toast.error(
                        error?.response?.data?.message ||
                            'Không tạo được link mua. Vui lòng thử sản phẩm khác.'
                    );
                }
            }
        } catch (error) {
            console.log(error);
            setOnGetLink(false);
        }
        setOnGetLink(false);
    };

    // Tạo (hoặc lấy lại) affiliate link RioHub cho TikTok link mode.
    const ensureAffiliateLink = async () => {
        if (cachedAffLink) return cachedAffLink;
        const { data } = await riohubAffiliateApi.createAffiliateLink({
            productId: productInfo?.itemId,
            productUrl: productLink,
        });
        const link = data?.data?.affiliateLink || "";
        if (link) setCachedAffLink(link);
        return link;
    };

    if (isLoading)
        return (
            <div className="flex gap-1 w-full justify-between">
                <div className="w-1/2 aspect-square rounded-lg bg-surface-raised animate-hightLight"></div>
                <div className="w-1/2 aspect-square rounded-lg">
                    <div className="h-full w-full flex flex-col justify-between gap-1">
                        <div className="w-full h-3/6 bg-surface-raised rounded-lg animate-hightLight"></div>
                        <div className="w-full h-1/6 bg-surface-raised rounded-lg animate-hightLight"></div>
                        <div className="w-full h-2/6 bg-surface-raised rounded-lg animate-hightLight"></div>
                    </div>
                </div>
            </div>
        );
    return (
        <div>
            {/* 
            {productInfo.isLimitCap && (
                <div className="flex gap-2 p-1 bg-primary-300 mb-1 rounded-md">
                    <p className="flex gap-1 items-center">
                        <FontAwesomeIcon icon={faWarning} />
                        <span>{`Sản phẩm giới hạn hoa hồng!`}</span>
                    </p>
                    <span>
                        {productInfo.cap
                            ? `Tối đa ${formattedMoney.format(productInfo.cap)} mỗi đơn!`
                            : "Mức hoàn tiền tối đa đang cập nhật"}
                    </span>
                </div>
            )}
            */}
            <div className="flex text-xs md:text-base gap-3 relative w-full bg-surface rounded-lg h-full">
                <div className="flex justify-center flex-1">
                    <div className="w-full aspect-square overflow-hidden rounded-lg border border-black">
                        <img
                            className="h-full w-full object-cover"
                            src={imageUrl || '/img/brand-logo/longhousee.png'}
                            alt={productName || 'no-info'}
                        />
                    </div>
                </div>
                <div className="flex-1 flex flex-col justify-between">
                    <div className="flex flex-col p-1 justify-between">
                        <span className="overflow-hidden text-ellipsis line-clamp-3 font-semibold">
                            {productName ? productName.toUpperCase() : ''}
                        </span>
                        {/* 
                        <div className="flex flex-col justify-between">
                            <span className="overflow-hidden text-ellipsis line-clamp-1">
                                {shopName || 'Shop chưa xác định'}
                            </span>
                            {sales && (
                                <span className="whitespace-nowrap">
                                    Lượt bán:{' '}
                                    <strong>{`${convertToK(sales)}`}</strong>
                                </span>
                            )}
                        </div>
                        */}
                        <div className="flex items-center gap-2">
                            <span className="text-base font-bold leading-5">
                                {price ? formattedMoney.format(price) : ''}
                            </span>
                            {price > 0 && productInfo?.itemId && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowWatchModal(true); }}
                                    className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                                        isWatching ? 'bg-green-100' : 'bg-surface-muted hover:bg-primary-50'
                                    }`}
                                    title={isWatching ? 'Đang theo dõi giá' : 'Theo dõi giá'}
                                >
                                    <FontAwesomeIcon
                                        icon={faBell}
                                        className={`text-xs ${isWatching ? 'text-green-500' : 'text-content-3'}`}
                                    />
                                </button>
                            )}
                        </div>

                        {platform === "shopee" ? (
                            <div className={`${buyBtnTextColor || "text-primary-600"}`}>
                                {Number(commission) > 0 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setShowBreakdown(v => !v)}
                                        className="flex items-center gap-1"
                                        aria-expanded={showBreakdown}
                                    >
                                        <span>Hoàn tiền đến</span>
                                        <strong className="font-bold text-base leading-3">
                                            {formattedMoney.format(commission)}
                                        </strong>
                                        <FontAwesomeIcon
                                            icon={faChevronDown}
                                            className={`ml-1 transition-transform ${showBreakdown ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    {showBreakdown && (
                                        <div className="text-xs mt-1">
                                            Shopee max {formattedMoney.format(shopeeComFinal)} + Shop {formattedMoney.format(sellerComFinal)}
                                        </div>
                                    )}
                                </>
                                ) : (
                                <strong className="font-bold">
                                    {productName ? 
                                        `Sản phẩm này không được hoàn tiền` : 
                                        `Vẫn được ghi nhận hoàn tiền nếu đơn hàng hợp lệ. Do hệ thống quá tải nên chưa hiển thị được chi tiết số liệu hoàn tiền.`}
                                </strong>
                                )}
                            </div>
                        ) : (
                            <span className={`${buyBtnTextColor || "text-primary-600"}`}>
                                {Number(commission) > 0 ? (
                                <>
                                    Hoàn tiền đến:
                                    <strong className="font-bold text-base leading-3">
                                    {formattedMoney.format(commission)}
                                    </strong>
                                </>
                            ) : (
                                <strong className="font-bold">
                                    {productName ? 
                                        `Sản phẩm này không được hoàn tiền` : 
                                        `Vẫn được ghi nhận hoàn tiền nếu đơn hàng hợp lệ. Do hệ thống quá tải nên chưa hiển thị được chi tiết số liệu hoàn tiền.`}
                                </strong>
                            )}
                            </span>
                        )}
                        {/* Sàn đang thưởng thêm hoa hồng cho SP này — khoản này nằm TRONG
                            số hoàn tiền phía trên, không phải cộng thêm nữa. CHỮ DO BE ĐẨY
                            (admin sửa trong trang cấu hình mua TikTok), web không set cứng
                            câu nào — set cứng thì đổi lời phải build lại web. */}
                        {Number(commission) > 0 && !!productNote && (
                            <p className="mt-1 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">
                                {productNote}
                            </p>
                        )}
                        {Number(commission) > 0 && !!platformBonusNote && (
                            <p className="mt-1 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">
                                {platformBonusNote}
                            </p>
                        )}
                        {Number(commission) > 0 && <AppX2CashbackNote />}
                        {/* Thưởng thêm của đợt — đặt cạnh nudge X2 vì cùng nói về
                            khuyến mãi hoàn tiền. Màu primary chứ không phải màu của
                            dòng % hoàn tiền: hai khoản KHÁC nhau. */}
                        {!!rewardNote && (
                            <p className="mt-1 rounded-lg bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-600">
                                🎁 {rewardNote}
                            </p>
                        )}
                    </div>
                    {!onlyInfo && (
                        <div className="flex flex-col gap-1 justify-end">
                            <button
                                onClick={handleSubmit}
                                className={`p-2 md:p-3 ${
                                    buyBtnBgColor || 'bg-primary-600'
                                } ${
                                    cashBackTextColor || 'text-white'
                                } rounded bottom-2 right-2 w-full font-bold`}
                            >
                                MUA NGAY
                            </button>
                            {/* THƯỞNG THÊM HOA HỒNG (chia sẻ link) — shopee/lazada + tiktok link mode */}
                            {(platform !== 'tiktok' || tiktokLinkMode) && (
                                <div className="relative rounded overflow-hidden">
                                    {/* <TopToast
                                        copied={copied}
                                        setCopied={setCopied}
                                    />
                                    <button
                                        onClick={onCopy}
                                        className="p-2 md:p-3 rounded-md w-full h-full border  bg-gray-500 text-white"
                                    >
                                        {onCopyLink ? (
                                            <span>Đang lấy...</span>
                                        ) : link ? (
                                            <div className="flex justify-center items-center gap-1 ">
                                                <span>Đã sao chép</span>
                                                <FontAwesomeIcon
                                                    icon={faCoins}
                                                />
                                            </div>
                                        ) : (
                                            <span>
                                                THƯỞNG THÊM HOA HỒNG{' '}
                                                <FontAwesomeIcon
                                                    icon={faCoins}
                                                />
                                            </span>
                                        )}
                                    </button> */}
                                    <button
                                        onClick={() => setOpenModalEarn(true)}
                                        className="p-2 md:p-3 rounded-md w-full h-full border  bg-gray-500 text-white"
                                    >
                                        <span>
                                            THƯỞNG THÊM HOA HỒNG{' '}
                                            <FontAwesomeIcon icon={faCoins} />
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <Modal isOpen={onGetLink}>
                    <RedirectPopup platform={platform} />
                </Modal>
                <Modal isOpen={openModal}>
                    <JoinGroup onClose={() => setOpenModal(false)} />
                </Modal>
                <Modal isOpen={openModalEarn}>
                    <EarnCommission
                        setOpenModal={setOpenModalEarn}
                        platform={platform}
                        link={productLink}
                        productInfo={productInfo}
                    />
                </Modal>
            </div>
            <PriceWatchAlertModal
                visible={showWatchModal}
                currentPrice={price || 0}
                onConfirm={handleAddWatch}
                onCancel={() => setShowWatchModal(false)}
            />
        </div>
    );
}

export default ProductCardMain;

export { clickAffLink };